import { useDeferredValue, useEffect, useState } from 'react';
import LoadingState from '../../components/common/LoadingState';
import { useAuth } from '../../hooks/useAuth';
import {
  createAuthorCreator,
  deleteAuthorCreator,
  listAuthorCreators,
  STAFF_PROFILE_CATEGORIES,
  updateAuthorCreator,
} from '../../services/authorCreatorService';
import { deleteStoragePath, uploadContentFile } from '../../services/mediaService';
import { USER_ROLES } from '../../services/userService';
import { formatDate } from '../../utils/time';

const EMPTY_FORM_VALUES = {
  id: '',
  name: '',
  category: 'Education Team',
  profile: '',
  website: '',
  projectTitleRole: '',
  socialMedia: '',
  headshotUrl: '',
  headshotPath: '',
  bio: '',
  threeFunFacts: '',
};

function toFormValues(authorCreator) {
  if (!authorCreator) {
    return EMPTY_FORM_VALUES;
  }

  return {
    id: authorCreator.id || '',
    name: authorCreator.name || '',
    category: authorCreator.category || 'Education Team',
    profile: authorCreator.profile || '',
    website: authorCreator.website || '',
    projectTitleRole: authorCreator.projectTitleRole || '',
    socialMedia: authorCreator.socialMedia || '',
    headshotUrl: authorCreator.headshotUrl || '',
    headshotPath: authorCreator.headshotPath || '',
    bio: authorCreator.bio || '',
    threeFunFacts: authorCreator.threeFunFacts || '',
  };
}

export default function DashboardAuthorCreatorsPage() {
  const { user, profile } = useAuth();
  const [authorCreators, setAuthorCreators] = useState([]);
  const [formValues, setFormValues] = useState(EMPTY_FORM_VALUES);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadStatus, setUploadStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [headshotFile, setHeadshotFile] = useState(null);

  const deferredSearchTerm = useDeferredValue(searchTerm);
  const canDelete = profile?.role === USER_ROLES.ADMIN;

  useEffect(() => {
    let isSubscribed = true;

    async function loadAuthorCreators() {
      setLoading(true);
      setErrorMessage('');

      try {
        const result = await listAuthorCreators();
        if (!isSubscribed) {
          return;
        }

        setAuthorCreators(result);
      } catch (error) {
        if (!isSubscribed) {
          return;
        }

        setErrorMessage(error.message ?? 'Failed to load staff profiles.');
      } finally {
        if (isSubscribed) {
          setLoading(false);
        }
      }
    }

    loadAuthorCreators();

    return () => {
      isSubscribed = false;
    };
  }, []);

  async function reloadAuthorCreators() {
    const result = await listAuthorCreators();
    setAuthorCreators(result);
    return result;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!user) {
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setUploadStatus('');

    try {
      const payload = {
        name: formValues.name,
        category: formValues.category,
        profile: formValues.profile,
        website: formValues.website,
        projectTitleRole: formValues.projectTitleRole,
        socialMedia: formValues.socialMedia,
        bio: formValues.bio,
        threeFunFacts: formValues.threeFunFacts,
      };

      let documentId = formValues.id;

      if (formValues.id) {
        await updateAuthorCreator(formValues.id, payload, user);
      } else {
        documentId = await createAuthorCreator(payload, user);
      }

      if (headshotFile) {
        if (formValues.headshotPath) {
          await deleteStoragePath(formValues.headshotPath);
        }

        const upload = await uploadContentFile({
          collectionKey: 'authorCreators',
          documentId,
          folder: 'headshot',
          file: headshotFile,
          onProgress: (progress) => setUploadStatus(`Uploading headshot (${progress}%)`),
        });

        await updateAuthorCreator(
          documentId,
          {
            headshotUrl: upload.downloadURL,
            headshotPath: upload.fullPath,
          },
          user
        );
      }

      const refreshed = await reloadAuthorCreators();
      if (formValues.id) {
        const current = refreshed.find((staffProfile) => staffProfile.id === documentId) || null;
        setFormValues(toFormValues(current));
      } else {
        setFormValues(EMPTY_FORM_VALUES);
      }
      setHeadshotFile(null);
      setUploadStatus('');
    } catch (error) {
      setErrorMessage(error.message ?? 'Failed to save staff profile.');
      setUploadStatus('');
    } finally {
      setIsSaving(false);
    }
  }

  function handleEdit(authorCreator) {
    setFormValues(toFormValues(authorCreator));
    setHeadshotFile(null);
    setUploadStatus('');
    setErrorMessage('');
  }

  function handleCreateNew() {
    setFormValues(EMPTY_FORM_VALUES);
    setHeadshotFile(null);
    setUploadStatus('');
    setErrorMessage('');
  }

  async function handleDelete(authorCreator) {
    if (!canDelete) {
      setErrorMessage('Only admin users can delete staff profiles.');
      return;
    }

    const shouldDelete = window.confirm(
      `Delete "${authorCreator.name}" from staff profiles?`
    );

    if (!shouldDelete) {
      return;
    }

    setDeletingId(authorCreator.id);
    setErrorMessage('');

    try {
      if (authorCreator.headshotPath) {
        await deleteStoragePath(authorCreator.headshotPath);
      }

      await deleteAuthorCreator(authorCreator.id);
      const refreshed = await reloadAuthorCreators();
      if (formValues.id === authorCreator.id) {
        setFormValues(EMPTY_FORM_VALUES);
        setHeadshotFile(null);
      } else if (formValues.id) {
        const current =
          refreshed.find((record) => record.id === formValues.id) || null;
        setFormValues(toFormValues(current));
      }
    } catch (error) {
      setErrorMessage(error.message ?? 'Failed to delete staff profile.');
    } finally {
      setDeletingId('');
    }
  }

  const filteredAuthorCreators = authorCreators.filter((authorCreator) => {
    const token = deferredSearchTerm.trim().toLowerCase();
    if (!token) {
      return true;
    }

    return (
      authorCreator.name?.toLowerCase().includes(token) ||
      authorCreator.category?.toLowerCase().includes(token) ||
      authorCreator.profile?.toLowerCase().includes(token) ||
      authorCreator.website?.toLowerCase().includes(token) ||
      authorCreator.projectTitleRole?.toLowerCase().includes(token) ||
      authorCreator.socialMedia?.toLowerCase().includes(token) ||
      authorCreator.headshotUrl?.toLowerCase().includes(token) ||
      authorCreator.bio?.toLowerCase().includes(token)
      || authorCreator.threeFunFacts?.toLowerCase().includes(token)
    );
  });

  if (loading) {
    return <LoadingState label="Loading staff profiles..." />;
  }

  return (
    <div className="page-stack">
      <section className="section-heading">
        <div>
          <p className="eyebrow">Dashboard</p>
          <h1>Staff Profiles</h1>
          <p>
            Manage non-login public profile data for staff/team members.
            Website admin login users are managed separately.
          </p>
        </div>
      </section>

      {errorMessage ? <p className="form-error">{errorMessage}</p> : null}
      {uploadStatus ? <p className="upload-status">{uploadStatus}</p> : null}

      <form className="editor-form" onSubmit={handleSubmit}>
        <div className="editor-form-header">
          <h3>{formValues.id ? 'Edit Staff Profile' : 'Create Staff Profile'}</h3>
          {formValues.id ? (
            <button type="button" className="button-link" onClick={handleCreateNew}>
              Create new staff profile
            </button>
          ) : null}
        </div>

        <label>
          Name
          <input
            value={formValues.name}
            onChange={(event) =>
              setFormValues((current) => ({ ...current, name: event.target.value }))
            }
            required
          />
        </label>

        <label>
          Category
          <select
            value={formValues.category}
            onChange={(event) =>
              setFormValues((current) => ({ ...current, category: event.target.value }))
            }
          >
            {STAFF_PROFILE_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </label>

        <label>
          Profile (optional)
          <input
            value={formValues.profile}
            onChange={(event) =>
              setFormValues((current) => ({ ...current, profile: event.target.value }))
            }
          />
        </label>

        <label>
          Website (optional)
          <input
            value={formValues.website}
            onChange={(event) =>
              setFormValues((current) => ({ ...current, website: event.target.value }))
            }
            placeholder="https://..."
          />
        </label>

        <label>
          Project title/role (optional)
          <input
            value={formValues.projectTitleRole}
            onChange={(event) =>
              setFormValues((current) => ({
                ...current,
                projectTitleRole: event.target.value,
              }))
            }
          />
        </label>

        <label>
          Social media (optional)
          <textarea
            rows={3}
            value={formValues.socialMedia}
            onChange={(event) =>
              setFormValues((current) => ({
                ...current,
                socialMedia: event.target.value,
              }))
            }
          />
        </label>

        <label>
          Headshot image (optional)
          <input
            type="file"
            accept="image/*"
            onChange={(event) => setHeadshotFile(event.target.files?.[0] || null)}
          />
        </label>

        {headshotFile ? (
          <p>Selected image: {headshotFile.name}</p>
        ) : null}

        {formValues.headshotUrl ? (
          <div className="state-card">
            <p>Current headshot</p>
            <img
              src={formValues.headshotUrl}
              alt={`${formValues.name || 'Author'} headshot`}
              style={{ maxWidth: '220px', borderRadius: '10px' }}
            />
          </div>
        ) : null}

        <label>
          Headshot image URL (read-only)
          <input
            value={formValues.headshotUrl}
            readOnly
          />
        </label>

        <label>
          Bio (optional)
          <textarea
            rows={4}
            value={formValues.bio}
            onChange={(event) =>
              setFormValues((current) => ({ ...current, bio: event.target.value }))
            }
          />
        </label>

        <label>
          Three fun facts (optional)
          <textarea
            rows={4}
            value={formValues.threeFunFacts}
            onChange={(event) =>
              setFormValues((current) => ({
                ...current,
                threeFunFacts: event.target.value,
              }))
            }
          />
        </label>

        <button type="submit" disabled={isSaving}>
          {isSaving
            ? 'Saving profile...'
            : formValues.id
              ? 'Update Staff Profile'
              : 'Create Staff Profile'}
        </button>
      </form>

      <div className="dashboard-toolbar">
        <input
          placeholder="Search staff profiles..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
      </div>

      {filteredAuthorCreators.length === 0 ? (
        <div className="state-card">
          <p>No staff profiles found.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="content-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Role/Profile</th>
                <th>Website</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAuthorCreators.map((authorCreator) => (
                <tr key={authorCreator.id}>
                  <td>{authorCreator.name || 'No name'}</td>
                  <td>{authorCreator.category || 'Uncategorized'}</td>
                  <td>{authorCreator.projectTitleRole || authorCreator.profile || 'No role/profile'}</td>
                  <td>
                    {authorCreator.website ? (
                      <a href={authorCreator.website} target="_blank" rel="noreferrer noopener">
                        {authorCreator.website}
                      </a>
                    ) : (
                      'No website'
                    )}
                  </td>
                  <td>{formatDate(authorCreator.updatedAt || authorCreator.createdAt)}</td>
                  <td className="table-actions">
                    <button type="button" onClick={() => handleEdit(authorCreator)}>
                      Edit
                    </button>
                    {canDelete ? (
                      <button
                        type="button"
                        className="danger"
                        onClick={() => handleDelete(authorCreator)}
                        disabled={deletingId === authorCreator.id}
                      >
                        {deletingId === authorCreator.id ? 'Deleting...' : 'Delete'}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
